import React, { useState } from "react";
import { T } from "../lib/theme";
import { fmt } from "../lib/utils";
import { resolveValue } from "../lib/valuation";
import { Spin } from "./Atoms";
import { ScannerModal } from "./ScannerModal";

const BLANK = { nickname: "", year: "", make: "", model: "", trim: "", mileage: "", vin: "", value: "", loan: "", rate: "", payment: "" };
// Matches VehicleModal.jsx's own REQUIRED_ADD_KEYS exactly — kept as its
// own constant here too, same rationale: the required set is defined in
// exactly one place regardless of render order. Nickname is deliberately
// NOT in this list — it self-generates at submit time (buildAutoNickname
// below), same fallback VehicleModal already uses.
const REQUIRED_KEYS = ["vin", "year", "make", "model", "mileage"];
const REQUIRED_FIELD_LABELS = { vin: "VIN", year: "year", make: "make", model: "model", mileage: "mileage" };

// "VIN, year, make, and mileage" — the exact list of fields the user is
// still missing, drives the disabled-submit-button explanation so it's
// never a silent, unexplained disabled state.
function formatList(arr) {
  if (arr.length === 0) return "";
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
  return `${arr.slice(0, -1).join(", ")}, and ${arr[arr.length - 1]}`;
}

// Guided first-run flow for a brand-new demo signup with zero
// vehicles — Session 2a of the spend-control-lock work. Shown by
// HomeFleetApp in place of the normal fleet view; see that file for
// the showWalkthrough gating logic (deliberately decided once per
// mount, not continuously re-derived, so completing this flow doesn't
// yank the summary step out from under the user the instant their
// vehicle count goes from 0 to 1).
//
// Deliberately does its own fetch()s (POST /api/vehicles, then POST
// /api/vehicles/:id/enrich) rather than reusing HomeFleetApp's own
// addVehicle() — that function ties its background enrich call to
// dataMode==="live", which doesn't apply here: this flow's whole
// purpose is triggering the one real, capped pull a demo account gets,
// on purpose, regardless of what the ambient Demo/Live toggle claims.
//
// ScannerModal is embedded with mode="live" forced, not the ambient
// dataMode — without that, ScannerModal's own lookupVIN()/lookupPlate()
// route to a 3-entry canned demo table (src/lib/vehicleLookup.js's
// VIN_DB/PLATE_DB) instead of the real, free NHTSA VIN decode this
// step exists to offer. Forcing "live" here doesn't reopen any spend
// gap — VIN decode is free, and a plate lookup was already confirmed
// allowed for demo accounts (same cap as everyone else) before this
// flow was built.
export function DemoWalkthrough({onAddVehicle,onComplete,customerName}){
  const[step,setStep]=useState("welcome"); // welcome | lookup | review | summary
  const[showScanner,setShowScanner]=useState(false);
  const[f,setF]=useState(BLANK);
  const[submitting,setSubmitting]=useState(false);
  const[submitError,setSubmitError]=useState("");
  const[vehicle,setVehicle]=useState(null); // the added vehicle; financials merge in later, in place, once the background enrich call resolves
  const[enrichState,setEnrichState]=useState("pending"); // "pending" | "done" | "failed" — drives the summary screen's Market Value box; only meaningful once step==="summary"
  const[enrichNote,setEnrichNote]=useState(""); // set if the real pull was refused/failed — still shown, not hidden
  const[vinSkipped,setVinSkipped]=useState(false); // true only via ScannerModal's "I don't have my VIN" — drives the reassurance message on the review step's VIN field

  const F=k=>e=>setF(p=>({...p,[k]:e.target.value}));
  const filled=k=>String(f[k]??"").trim()!=="";
  const ok=REQUIRED_KEYS.every(filled);
  const missingRequired=REQUIRED_KEYS.filter(k=>!filled(k));

  // Same 4-tier fallback as VehicleModal.jsx's buildAutoNickname — kept
  // here as a separate copy (not imported) since these are two independent
  // components with no shared parent module, same reasoning enrichFinancials.js
  // gives for its own small duplicated formulas.
  const buildAutoNickname=()=>{
    const year=String(f.year??"").trim();
    const model=String(f.model??"").trim();
    const name=customerName&&customerName.trim();
    if(name&&year&&model)return`${name}'s ${year} ${model}`;
    if(year&&model)return`My ${year} ${model}`;
    if(name)return`${name}'s Vehicle`;
    return"My Vehicle";
  };

  const startManual=()=>{setF(BLANK);setVinSkipped(false);setStep("review");};
  const onScanResult=data=>{setF(p=>({...p,...data,nickname:p.nickname||`${data.year||""} ${data.make||""} ${data.model||""}`.trim()}));setVinSkipped(false);setShowScanner(false);setStep("review");};
  // "I don't have my VIN" inside ScannerModal's VIN tab — skips the lookup
  // entirely rather than leaving the user stuck retyping something they
  // don't have. Blank fields, same as startManual, plus the reassurance
  // flag the review step's VIN field reads. VIN is hard-required now (matching
  // VehicleModal), so the message here is honest about still needing it below,
  // not a "you can skip this" claim.
  const onNoVin=()=>{setF(BLANK);setVinSkipped(true);setShowScanner(false);setStep("review");};

  const submit=async()=>{
    setSubmitting(true);setSubmitError("");
    try{
      const payload={...f,nickname:f.nickname.trim()||buildAutoNickname()};
      const res=await fetch("/api/vehicles",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)});
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not save your vehicle.");
      onAddVehicle(body); // lets HomeFleetApp prepend it to its own vehicles state right away

      // The vehicle is saved — move to the summary screen immediately
      // rather than waiting on enrichment. Previously this awaited the
      // enrich call inline, which meant a slow or stalled response (no
      // timeout existed anywhere in that chain) left `submitting` stuck
      // true forever, with no way off this screen short of a page reload
      // (see the Add Vehicle demo-hang fix). The enrich call still fires
      // below — just as a background fetch, same fire-and-forget shape
      // HomeFleetApp.addVehicle() already uses for its own Live-mode
      // enrich call — so a slow/failed pull can no longer block this flow.
      setVehicle(body);
      setEnrichState("pending");
      setStep("summary");

      // The one real pull this account gets — fires unconditionally,
      // not gated by dataMode. A refusal here (the account already had
      // its lifetime pull used some other way, or the daily household
      // cap is shared and already spent) is shown, not swallowed —
      // Session 2b's contextual prompts key off exactly this reason
      // field, so it's surfaced plainly rather than papered over with
      // a generic message. Resolves in place on the already-visible
      // summary screen; failing (or never resolving fast enough for the
      // human sitting there) no longer blocks anything.
      fetch(`/api/vehicles/${body.id}/enrich`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({fields:["value"]})})
        .then(async eRes=>{
          const eBody=await eRes.json().catch(()=>({}));
          if(eRes.ok&&eBody.results?.value){
            setVehicle(v=>v?{...v,financials:{...v.financials,value:eBody.results.value}}:v);
            setEnrichState("done");
          }else{
            setEnrichNote(eBody.error||"Could not fetch a real value estimate right now.");
            setEnrichState("failed");
          }
        })
        .catch(()=>{
          setEnrichNote("Could not fetch a real value estimate right now.");
          setEnrichState("failed");
        });
    }catch(e){
      setSubmitError(e.message||"Could not save your vehicle.");
    }
    setSubmitting(false);
  };

  const inp={width:"100%",boxSizing:"border-box",border:`1.5px solid ${T.border}`,borderRadius:9,padding:"9px 12px",fontSize:14,color:T.text,background:T.card,outline:"none"};
  const label={fontSize:12,fontWeight:600,color:T.textMd,display:"block",marginBottom:4};
  const card={maxWidth:520,margin:"40px auto",background:T.card,borderRadius:16,border:`1px solid ${T.border}`,boxShadow:T.shadow,padding:"28px 26px"};

  if(step==="welcome")return(
    <div style={card}>
      <div style={{fontSize:36,marginBottom:10}}>🚘</div>
      <div style={{fontSize:20,fontWeight:800,color:T.text,marginBottom:8}}>Welcome to HomeFleet</div>
      <div style={{fontSize:14,color:T.textMd,lineHeight:1.6,marginBottom:22}}>Let's add your first vehicle and see real data about it — a real market value, recall check, and maintenance schedule, built from what you tell us.</div>
      <button onClick={()=>setStep("lookup")} style={{background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:10,padding:"12px 26px",fontSize:14,fontWeight:700,cursor:"pointer"}}>Get Started →</button>
    </div>
  );

  if(step==="lookup")return(
    <div style={card}>
      <div style={{fontSize:14,fontWeight:700,color:T.teal,marginBottom:6}}>Step 1 of 2</div>
      <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:8}}>Add your first vehicle</div>
      <div style={{fontSize:13,color:T.textMd,lineHeight:1.6,marginBottom:20}}>Photograph or type the VIN (usually inside the driver's door frame) — that's free and gives the most accurate result. A license plate works too, but it draws from a smaller, separate lookup pool, so we'd rather save that unless you don't have the VIN handy.</div>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        <button onClick={()=>setShowScanner(true)} style={{background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:10,padding:"12px 0",fontSize:14,fontWeight:700,cursor:"pointer"}}>Scan or Enter VIN</button>
        <button onClick={startManual} style={{background:T.card,color:T.textMd,border:`1px solid ${T.border}`,borderRadius:10,padding:"11px 0",fontSize:13,fontWeight:600,cursor:"pointer"}}>Enter details manually instead</button>
        <button onClick={()=>setStep("welcome")} style={{background:"none",color:T.textSm,border:"none",borderRadius:10,padding:"6px 0",fontSize:13,fontWeight:600,cursor:"pointer"}}>← Back</button>
      </div>
      {showScanner&&<ScannerModal mode="live" onResult={onScanResult} onNoVin={onNoVin} onClose={()=>setShowScanner(false)}/>}
    </div>
  );

  if(step==="review")return(
    <div style={card}>
      <div style={{fontSize:14,fontWeight:700,color:T.teal,marginBottom:6}}>Step 2 of 2</div>
      <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:8}}>Check the details</div>
      <div style={{marginBottom:18,padding:"12px 14px",borderRadius:10,background:T.tealL,border:`1px solid ${T.tealBorder}`,fontSize:12,color:T.text,lineHeight:1.5}}>
        VIN, Year, Make, Model, and Mileage are required — they're what let us pull real market value, safety, and maintenance data for this vehicle. Nickname is optional; leave it blank and we'll generate one for you.
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:20}}>
        <div>
          <label style={label}>VIN<span style={{color:T.red}}> *</span></label>
          {vinSkipped&&<div style={{fontSize:12,color:T.teal,background:T.tealL,borderRadius:8,padding:"8px 11px",marginBottom:6,fontWeight:600}}>No problem — you'll just need to enter it below before continuing. Check your registration, insurance card, or the driver's-side door frame.</div>}
          <input value={f.vin} onChange={e=>setF(p=>({...p,vin:e.target.value.toUpperCase()}))} placeholder="17-character VIN" style={{...inp,fontFamily:"'DM Mono',monospace"}}/>
          <div style={{fontSize:10.5,color:T.textMd,fontWeight:600,marginTop:4,lineHeight:1.4}}>Required — the most accurate way to verify specs and check for open recalls</div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:10}}>
          <div>
            <label style={label}>Year<span style={{color:T.red}}> *</span></label>
            <input value={f.year} onChange={F("year")} style={inp}/>
            <div style={{fontSize:10.5,color:T.textMd,fontWeight:600,marginTop:4,lineHeight:1.4}}>Required — used for accurate valuation, maintenance schedules, and recalls</div>
          </div>
          <div>
            <label style={label}>Make<span style={{color:T.red}}> *</span></label>
            <input value={f.make} onChange={F("make")} style={inp}/>
            <div style={{fontSize:10.5,color:T.textMd,fontWeight:600,marginTop:4,lineHeight:1.4}}>Required — used to find real maintenance schedules, recalls, and safety ratings</div>
          </div>
        </div>
        <div>
          <label style={label}>Model<span style={{color:T.red}}> *</span></label>
          <input value={f.model} onChange={F("model")} style={inp}/>
          <div style={{fontSize:10.5,color:T.textMd,fontWeight:600,marginTop:4,lineHeight:1.4}}>Required — combined with Make for accurate market value and safety data</div>
        </div>
        <div>
          <label style={label}>Trim (optional)</label>
          <input value={f.trim} onChange={F("trim")} style={inp}/>
          <div style={{fontSize:10.5,color:T.textSm,marginTop:4,lineHeight:1.4}}>Improves market value accuracy</div>
        </div>
        <div>
          <label style={label}>Mileage<span style={{color:T.red}}> *</span></label>
          <input value={f.mileage} onChange={F("mileage")} type="number" placeholder="e.g. 42000" style={inp}/>
          <div style={{fontSize:10.5,color:T.textMd,fontWeight:600,marginTop:4,lineHeight:1.4}}>Required — used for accurate valuation and cost-of-ownership estimates</div>
        </div>
        <div>
          <label style={label}>Nickname (optional)</label>
          <input value={f.nickname} onChange={F("nickname")} placeholder="e.g. My Car" style={inp}/>
          <div style={{fontSize:10.5,color:T.textSm,marginTop:4,lineHeight:1.4}}>Leave blank and we'll name it for you</div>
        </div>
      </div>
      <div style={{fontSize:12,fontWeight:700,color:T.textSm,textTransform:"uppercase",letterSpacing:".06em",borderTop:`1px solid ${T.border}`,paddingTop:16,marginBottom:2}}>Financing (optional)</div>
      <div style={{fontSize:12,color:T.textSm,lineHeight:1.5,marginBottom:12}}>Know these numbers? Enter them for real figures — anything you skip gets a clearly-labeled estimate instead.</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:20}}>
        <div><label style={label}>Purchase Price ($)</label><input value={f.value} onChange={F("value")} type="number" placeholder="e.g. 28500" style={inp}/></div>
        <div><label style={label}>Loan Balance ($)</label><input value={f.loan} onChange={F("loan")} type="number" placeholder="e.g. 15000" style={inp}/></div>
        <div><label style={label}>Interest Rate (%)</label><input value={f.rate} onChange={F("rate")} type="number" placeholder="e.g. 6.5" style={inp}/></div>
        <div><label style={label}>Monthly Payment ($)</label><input value={f.payment} onChange={F("payment")} type="number" placeholder="e.g. 420" style={inp}/></div>
      </div>
      {submitError&&<div style={{fontSize:13,color:T.red,background:T.redL,borderRadius:8,padding:"9px 12px",marginBottom:14}}>{submitError}</div>}
      {/* `vehicle` is only ever set once, inside submit()'s own success
          path — already a reliable "a POST /api/vehicles succeeded this
          session" signal, so it doubles as that flag here rather than
          introducing a second, redundant piece of state for the same
          fact. Reaching review with it already set (Summary → Back)
          means resubmitting would create a second vehicle — fields stay
          visible/editable for reference, but the submit button is
          replaced with a plain link back to the summary that's already
          real. The first-time path (the else branch) is untouched. */}
      {vehicle?(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div style={{fontSize:13,color:T.teal,background:T.tealL,borderRadius:8,padding:"10px 14px",fontWeight:600}}>You've already added this vehicle.</div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={()=>setStep("lookup")} style={{background:T.card,color:T.textMd,border:`1px solid ${T.border}`,borderRadius:10,padding:"11px 18px",fontSize:13,fontWeight:600,cursor:"pointer"}}>Back</button>
            <button onClick={()=>setStep("summary")} style={{flex:1,background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:10,padding:"11px 0",fontSize:14,fontWeight:700,cursor:"pointer"}}>Go to Summary →</button>
          </div>
        </div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{display:"flex",gap:10}}>
            <button onClick={()=>setStep("lookup")} disabled={submitting} style={{background:T.card,color:T.textMd,border:`1px solid ${T.border}`,borderRadius:10,padding:"11px 18px",fontSize:13,fontWeight:600,cursor:submitting?"default":"pointer"}}>Back</button>
            <button onClick={submit} disabled={!ok||submitting} style={{flex:1,background:ok?`linear-gradient(135deg,${T.teal},#0f766e)`:T.btnDisabledBg,color:ok?"#fff":T.textSm,border:"none",borderRadius:10,padding:"11px 0",fontSize:14,fontWeight:700,cursor:ok&&!submitting?"pointer":"default",opacity:submitting?.7:1}}>{submitting?<span style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8}}><Spin sz={15} stroke="#fff" track="rgba(255,255,255,.4)"/>Adding your vehicle...</span>:"Add My Vehicle"}</button>
          </div>
          {!ok&&!submitting&&<div style={{fontSize:12,color:T.amber,textAlign:"center"}}>Enter your {formatList(missingRequired.map(k=>REQUIRED_FIELD_LABELS[k]))} to continue</div>}
        </div>
      )}
    </div>
  );

  if(step==="summary"&&vehicle){
    const val=resolveValue(vehicle);
    return(
      <div style={card}>
        <div style={{fontSize:32,marginBottom:8}}>✅</div>
        <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:4}}>{vehicle.nickname||`${vehicle.year} ${vehicle.make} ${vehicle.model}`} is on your dashboard</div>
        <div style={{fontSize:13,color:T.textSm,marginBottom:20}}>Here's what we found.</div>

        <div style={{background:T.slateL,borderRadius:12,border:`1px solid ${T.border}`,padding:"16px 18px",marginBottom:16}}>
          <div style={{fontSize:11,color:T.textSm,textTransform:"uppercase",letterSpacing:".05em",marginBottom:4}}>Estimated Market Value</div>
          {enrichState==="pending"?(
            <div style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:T.textMd,padding:"4px 0"}}><Spin sz={15}/>Getting your real market value estimate...</div>
          ):enrichState==="done"?(
            <>
              <div className="mono" style={{fontSize:26,fontWeight:800,color:T.text}}>{fmt(val.amount)}</div>
              <div style={{fontSize:12,color:T.textSm,marginTop:4}}>{val.source==="live"?`Sourced from ${val.origin} — real market data`:val.source==="researched"?`Researched via ${val.origin}`:"Our best estimate — no live pricing data was available for this one"}</div>
            </>
          ):(
            <div style={{fontSize:13,color:T.textMd}}>{enrichNote||"We couldn't get a value estimate this time — you can try again later from your dashboard."}</div>
          )}
        </div>

        <div style={{fontSize:13,color:T.textMd,lineHeight:1.7,marginBottom:22}}>
          Next, on your dashboard, you'll also see a <strong style={{color:T.text}}>recall check</strong> against NHTSA's live database and a <strong style={{color:T.text}}>maintenance schedule</strong> built for your vehicle — both free, no limit, right on the vehicle card.
        </div>

        <div style={{display:"flex",gap:10}}>
          <button onClick={()=>setStep("review")} style={{background:T.card,color:T.textMd,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px 18px",fontSize:13,fontWeight:600,cursor:"pointer"}}>Back</button>
          <button onClick={onComplete} style={{flex:1,background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:10,padding:"12px 0",fontSize:14,fontWeight:700,cursor:"pointer"}}>Go to My Dashboard →</button>
        </div>
      </div>
    );
  }

  return null;
}
